import { Controller } from "@hotwired/stimulus"

// Accessible tabs for the watch player's supporting content. The controller is
// intentionally data-only: richer recommendation metadata can change the panel
// contents later without changing how the player switches between them.
export default class extends Controller {
  static targets = ["tab", "panel", "relatedCount", "relatedContent"]
  static values = { relatedUrl: String }

  connect() {
    this.show(this.selectedTabName() || this.tabTargets[0]?.dataset.playerTab)
  }

  disconnect() {
    this.relatedRequest?.abort()
  }

  select(event) {
    this.show(event.currentTarget.dataset.playerTab)
  }

  openFromEvent(event) {
    this.show(event.detail?.tab)
    this.tabTargets.find((tab) => tab.dataset.playerTab === event.detail?.tab)?.focus()
  }

  async refreshRelated(event) {
    if (!this.hasRelatedUrlValue || !this.hasRelatedContentTarget) return

    const lessonId = event.detail?.lessonId
    const contextKey = lessonId ? String(lessonId) : 'whole-video'
    if (contextKey === this.relatedContextKey) return
    this.relatedContextKey = contextKey

    this.relatedRequest?.abort()
    const request = new AbortController()
    this.relatedRequest = request
    const url = new URL(this.relatedUrlValue, window.location.origin)
    if (lessonId) url.searchParams.set('video_lesson_id', lessonId)

    this.relatedContentTarget.setAttribute('aria-busy', 'true')
    try {
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: request.signal
      })
      if (!response.ok) throw new Error(`Related videos request failed: ${response.status}`)

      const template = document.createElement('template')
      template.innerHTML = (await response.text()).trim()
      const content = template.content.firstElementChild
      if (!content) return

      const count = Number.parseInt(content.dataset.relatedCount || '0', 10)
      this.relatedContentTarget.replaceChildren(content)
      if (this.hasRelatedCountTarget) {
        this.relatedCountTarget.textContent = ` · ${count}`
        this.relatedCountTarget.hidden = count === 0
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.relatedContextKey = undefined
        console.error('Could not refresh related videos:', error)
      }
    } finally {
      if (this.relatedRequest === request) this.relatedContentTarget.removeAttribute('aria-busy')
    }
  }

  keydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return

    event.preventDefault()
    const currentIndex = this.tabTargets.indexOf(event.currentTarget)
    let nextIndex

    if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = this.tabTargets.length - 1
    else if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabTargets.length
    else nextIndex = (currentIndex - 1 + this.tabTargets.length) % this.tabTargets.length

    const nextTab = this.tabTargets[nextIndex]
    this.show(nextTab.dataset.playerTab)
    nextTab.focus()
  }

  show(name) {
    if (!name) return

    this.tabTargets.forEach((tab) => {
      const selected = tab.dataset.playerTab === name
      tab.setAttribute('aria-selected', selected.toString())
      tab.tabIndex = selected ? 0 : -1
    })

    this.panelTargets.forEach((panel) => {
      panel.hidden = panel.dataset.playerPanel !== name
    })
  }

  selectedTabName() {
    return this.tabTargets.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.playerTab
  }
}
